import { useState, useEffect } from 'react'
import Head from 'next/head'
import Image from 'next/legacy/image'
import { useUserId } from '@nhost/nextjs'
import { useRouter } from 'next/router'
import { scroller } from 'react-scroll'

import useViewport from 'contexts/viewport'
import { usePfpBoughtByUserAndPaymentQuery, useUserMetadataQuery } from 'generated/graphql'
import ImageMasthead from 'components/image-masthead'
import SwitchProfile from 'components/switch-profile'
import { LoadingSpinner } from 'components/delayed'
import { Text } from 'components/atomic'
import DownloadIcon from 'components/icon/download'
import LinkIcon from 'components/icon/link'
import IconRarity from 'components/icon/rarity'

import TraitBackgroundIcon from 'components/icon/trait-background'
import TraitBodyIcon from 'components/icon/trait-body'
import TraitHeldItemIcon from 'components/icon/trait-held-item'
import TraitHelmetIcon from 'components/icon/trait-helmet'
import TraitLeftShoulderIcon from 'components/icon/trait-left-shoulder'
import TraitRightShoulderIcon from 'components/icon/trait-right-shoulder'
import TraitVisorCortexIcon from 'components/icon/trait-visor-cortex'
import TraitWeatherIcon from 'components/icon/trait-weather'
import TraitScoreBar from 'components/traits-rarity-bar'

import Database from './database'
import UGCPanel from 'containers/deadrop/ss6/ugc'
import Faq from 'containers/claws-nft/faq'

import { RarityColor } from 'lib/constants'
import download from 'utils/download'
import styles from './index.module.scss'
import { founderBlurDataURL } from 'constants/blurDataURL'

const FounderProfilePage = () => {
  const localhostOrNil = window.location.origin.indexOf('://localhost') > -1 ? 'https://vsm-nft-fe.vercel.app' : ''
  const router = useRouter()
  const { tab } = router.query
  const userId = useUserId()
  const { width: screenWidth, isMobile } = useViewport()

  const [nftsFounder, setNftsFounder] = useState<any>()
  const [walletAddr, setWalletAddr] = useState('')
  const [loadingOthers, setLoadingOthers] = useState(true)

  const { loading: userMetadataLoading, data } = useUserMetadataQuery()
  const nftsFounderQuery = usePfpBoughtByUserAndPaymentQuery({
    variables: {
      userId,
    },
  })

  useEffect(() => {
    if (!userMetadataLoading && !loadingOthers && !nftsFounderQuery.loading) {
      const hash = window.location.hash
      if (hash === '#download' || tab === 'snapshots') {
        scroller.scrollTo('snapshots', {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
        })
      }
    }
  }, [tab, userMetadataLoading, loadingOthers, nftsFounderQuery.loading])

  useEffect(() => {
    if (!nftsFounderQuery.loading && (!nftsFounderQuery.data || nftsFounderQuery.data.nfts_founder.length <= 0)) {
      router.push('/general-profile')
    }
  }, [nftsFounderQuery, router])

  useEffect(() => {
    if (!nftsFounderQuery.loading) {
      const bought = nftsFounderQuery.data?.nfts_founder?.[0]

      if (bought) {
        setNftsFounder(bought)
      }
    }
  }, [nftsFounderQuery])

  useEffect(() => {
    const response = data?.users_metadata[0]

    if (response) {
      if (response.wallet_stardust) setWalletAddr(response.wallet_stardust)
    }
    if (response) setLoadingOthers(false)
  }, [data])

  if (nftsFounderQuery.loading || userMetadataLoading || loadingOthers) {
    return (
      <ImageMasthead
        image={
          isMobile ? '/images/profile/access_pass_buy_prompt_mobile.png' : '/images/profile/access_pass_buy_prompt.png'
        }
        className="h-[750px] sm:h-[840px] sm:!max-h-[unset]"
      >
        <div className="relative h-[750px] pb-12 pt-24 sm:h-[840px] lg:pt-40">
          <LoadingSpinner isShown isFullScreen={false} />
        </div>
      </ImageMasthead>
    )
  }

  return (
    <>
      <Head>
        <title>Founder Profile | Midnight Society</title>
      </Head>
      <ImageMasthead
        image={screenWidth < 428 ? '/images/founder-profile/mobile_bg.png' : '/images/founder-profile/bg.png'}
        className="sm:!max-h-[unset]"
      >
        <SwitchProfile isFounderProfile={!!nftsFounder} />

        <div className={`px-5 pb-14 md:px-20 lg:pb-36 xl:mx-auto xl:max-w-[1440px] xl:px-32 2xl:px-40`}>
          {/* Mobile screen size hero */}
          <div className="flex flex-col items-center justify-center lg:hidden">
            <div className={styles.frame + ' h-[290px] w-[300px] sm:h-[345px] sm:w-[356px]'}>
              <div className="relative h-[83%] w-[83%]">
                <Image
                  src={`${localhostOrNil}/founders_pass_art/${nftsFounder?.onchain_tokenId}.jpg?thumb`}
                  layout="fill"
                  objectFit="cover"
                  alt="Access Pass Holder"
                  placeholder="blur"
                  blurDataURL={founderBlurDataURL}
                  unoptimized
                />
              </div>
            </div>
            {walletAddr && (
              <Text
                variant="p2"
                tag="p"
                className="mb-4 mt-6 text-center !font-medium text-secondary underline lg:text-left"
              >
                <a
                  href={`https://polygonscan.com/address/${walletAddr}#tokentxnsErc721`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Access Pass: {walletAddr.slice(0, 8)}...{walletAddr.slice(-6)}
                </a>
              </Text>
            )}

            <div className="flex w-full justify-center lg:w-fit">
              <div
                className="mr-3 flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-full bg-secondary"
                onClick={() => download(`/founders_pass_art/${nftsFounder?.onchain_tokenId}.jpg`)}
              >
                <DownloadIcon />
              </div>
              <a
                className="flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-full bg-secondary"
                href={`/founders_pass_details/${nftsFounder?.onchain_tokenId}`}
                target="_blank"
                rel="noreferrer"
              >
                <LinkIcon />
              </a>
            </div>
          </div>

          {/* Desktop screen size hero */}
          <div className="hidden lg:block">
            <div className="mb-6 flex flex-wrap-reverse items-center justify-center lg:flex-nowrap lg:justify-between">
              <Text
                variant="p2"
                tag="p"
                className="mr-2 text-center font-semibold tracking-wide text-white/70 lg:text-left"
              >
                Accessing Variant Database /// (1) File Found /// FILE#{nftsFounder?.onchain_tokenId} VARIANT{' '}
                <span className="uppercase">{nftsFounder?.name}</span>
              </Text>
              <div className="mb-7 flex w-full justify-center lg:mb-0 lg:w-fit">
                <div
                  className="mr-3 flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-full bg-secondary"
                  onClick={() => download(`/founders_pass_art/${nftsFounder?.onchain_tokenId}.jpg`)}
                >
                  <DownloadIcon />
                </div>
                <a
                  className="flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-full bg-secondary"
                  href={`/founders_pass_details/${nftsFounder?.onchain_tokenId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LinkIcon />
                </a>
              </div>
            </div>

            <div className="lg:flex">
              <div className={`hidden} mr-0 lg:mr-7 lg:block`}>
                <div className={styles.frame + ' w-[488px] lg:h-[368px] lg:w-[380px] 2xl:h-[473px] 2xl:w-[488px]'}>
                  <div className="relative h-[83%] w-[83%]">
                    <Image
                      src={`${localhostOrNil}/founders_pass_art/${nftsFounder?.onchain_tokenId}.jpg?thumb`}
                      layout="fill"
                      objectFit="cover"
                      alt="Access Pass Holder"
                      placeholder="blur"
                      blurDataURL={founderBlurDataURL}
                      unoptimized
                    />
                  </div>
                </div>
                {walletAddr && (
                  <Text
                    variant="p2"
                    tag="p"
                    className="my-2 ml-8 text-center !font-medium text-secondary underline lg:text-left"
                  >
                    <a
                      href={`https://polygonscan.com/address/${walletAddr}#tokentxnsErc721`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Access Pass: {walletAddr.slice(0, 8)}...{walletAddr.slice(-6)}
                    </a>
                  </Text>
                )}
              </div>
              <div className="mt-6 lg:mt-0">
                <div className="justify-center border-b-2 border-solid border-[#454340] sm:flex lg:justify-start">
                  <div className="mr-0 sm:mr-12 lg:mr-16 xl:mr-28">
                    <div className="mb-8">
                      <div className="mb-4 flex items-center">
                        <div>
                          <TraitBackgroundIcon
                            color={RarityColor[nftsFounder?.trait_background['rarity_class']]}
                            background={false}
                            width={'51'}
                            height={'61'}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                            background
                          </div>
                          <div
                            className="cursor-default text-xl font-semibold	uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                            title={nftsFounder?.trait_background['value']}
                          >
                            {nftsFounder?.trait_background['value']}
                          </div>
                        </div>
                      </div>
                      <TraitScoreBar
                        color={RarityColor[nftsFounder?.trait_background['rarity_class']]}
                        score={nftsFounder?.trait_background['rarity']}
                      />
                    </div>
                    <div className="mb-8">
                      <div className="mb-4 flex items-center">
                        <div>
                          <TraitBodyIcon
                            color={RarityColor[nftsFounder?.trait_body['rarity_class']]}
                            background={false}
                            width={'51'}
                            height={'61'}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                            body
                          </div>
                          <div
                            className=" cursor-default text-xl font-semibold	uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                            title={nftsFounder?.trait_body['value']}
                          >
                            {nftsFounder?.trait_body['value']}
                          </div>
                        </div>
                      </div>
                      <TraitScoreBar
                        color={RarityColor[nftsFounder?.trait_body['rarity_class']]}
                        score={nftsFounder?.trait_body['rarity']}
                      />
                    </div>
                    <div className="mb-8">
                      <div className="mb-4 flex items-center">
                        <div>
                          <TraitHelmetIcon
                            color={RarityColor[nftsFounder?.trait_helmet['rarity_class']]}
                            background={false}
                            width={'51'}
                            height={'60'}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                            helmet
                          </div>
                          <div
                            className="cursor-default text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                            title={nftsFounder?.trait_helmet['value']}
                          >
                            {nftsFounder?.trait_helmet['value']}
                          </div>
                        </div>
                      </div>
                      <TraitScoreBar
                        color={RarityColor[nftsFounder?.trait_helmet['rarity_class']]}
                        score={nftsFounder?.trait_helmet['rarity']}
                      />
                    </div>
                    <div className="mb-8">
                      <div className="mb-4 flex items-center">
                        <div>
                          <TraitVisorCortexIcon
                            color={RarityColor[nftsFounder?.trait_visor_cortex['rarity_class']]}
                            background={false}
                            width={'51'}
                            height={'60'}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="mb-1.5 cursor-default text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                            visor cortex
                          </div>
                          <div
                            className="cursor-default text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                            title={nftsFounder?.trait_visor_cortex['value']}
                          >
                            {nftsFounder?.trait_visor_cortex['value']}
                          </div>
                        </div>
                      </div>
                      <TraitScoreBar
                        color={RarityColor[nftsFounder?.trait_visor_cortex['rarity_class']]}
                        score={nftsFounder?.trait_visor_cortex['rarity']}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-8">
                      <div className="mb-4 flex items-center">
                        <div>
                          <TraitLeftShoulderIcon
                            color={RarityColor[nftsFounder?.trait_left_shoulder['rarity_class']]}
                            background={false}
                            width={'51'}
                            height={'60'}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                            LEFT SHOULDER
                          </div>
                          <div
                            className="text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                            title={nftsFounder?.trait_left_shoulder['value']}
                          >
                            {nftsFounder?.trait_left_shoulder['value']}
                          </div>
                        </div>
                      </div>
                      <TraitScoreBar
                        color={RarityColor[nftsFounder?.trait_left_shoulder['rarity_class']]}
                        score={nftsFounder?.trait_left_shoulder['rarity']}
                      />
                    </div>
                    <div className="mb-8">
                      <div className="mb-4 flex items-center">
                        <div>
                          <TraitRightShoulderIcon
                            color={RarityColor[nftsFounder?.trait_right_shoulder['rarity_class']]}
                            background={false}
                            width={'51'}
                            height={'60'}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                            RIGHT SHOULDER
                          </div>
                          <div
                            className="cursor-default text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                            title={nftsFounder?.trait_right_shoulder['value']}
                          >
                            {nftsFounder?.trait_right_shoulder['value']}
                          </div>
                        </div>
                      </div>
                      <TraitScoreBar
                        color={RarityColor[nftsFounder?.trait_right_shoulder['rarity_class']]}
                        score={nftsFounder?.trait_right_shoulder['rarity']}
                      />
                    </div>
                    <div className="mb-8">
                      <div className="mb-4 flex items-center">
                        <div>
                          <TraitWeatherIcon
                            color={RarityColor[nftsFounder?.trait_weather['rarity_class']]}
                            background={false}
                            width={'51'}
                            height={'60'}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="mb-1.5 cursor-default text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                            WEATHER
                          </div>
                          <div
                            className="text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                            title={nftsFounder?.trait_weather['value']}
                          >
                            {nftsFounder?.trait_weather['value']}
                          </div>
                        </div>
                      </div>
                      <TraitScoreBar
                        color={RarityColor[nftsFounder?.trait_weather['rarity_class']]}
                        score={nftsFounder?.trait_weather['rarity']}
                      />
                    </div>
                    <div className="mb-8">
                      <div className="mb-4 flex items-center">
                        <div>
                          <TraitHeldItemIcon
                            color={RarityColor[nftsFounder?.trait_held_item['rarity_class']]}
                            background={false}
                            width={'51'}
                            height={'60'}
                          />
                        </div>
                        <div className="ml-2">
                          <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                            HELD ITEM
                          </div>
                          <div
                            className="text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                            title={nftsFounder?.trait_held_item['value']}
                          >
                            {nftsFounder?.trait_held_item['value']}
                          </div>
                        </div>
                      </div>
                      <TraitScoreBar
                        color={RarityColor[nftsFounder?.trait_held_item['rarity_class']]}
                        score={nftsFounder?.trait_held_item['rarity']}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-10 flex flex-wrap-reverse items-center justify-center md:flex-nowrap lg:mt-6 lg:justify-start">
                  {walletAddr && (
                    <Text
                      variant="p2"
                      tag="div"
                      className="my-2 w-full text-center !font-medium text-secondary underline md:hidden"
                    >
                      <a
                        href={`https://polygonscan.com/address/${walletAddr}#tokentxnsErc721`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Access Pass: {walletAddr.slice(0, 8)}...{walletAddr.slice(-6)}
                      </a>
                    </Text>
                  )}
                  <div className="mx-auto my-4 sm:mx-0 sm:my-0">
                    <IconRarity color={RarityColor[nftsFounder?.trait_rarity_class['rarity_class']]} />
                  </div>
                  <div className="ml-0 w-full sm:ml-6 sm:w-fit">
                    <div className="mb-4 text-center text-[19px] leading-4 tracking-tighter text-secondary sm:mb-1.5 sm:text-left">
                      {nftsFounder?.trait_squad_role['value'] + ' ///'}
                    </div>
                    <Text variant="h3" className="text-center tracking-tight sm:text-left">
                      {nftsFounder?.name}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ImageMasthead>
      <div className="block px-5 pt-3 md:px-20 lg:hidden">
        <Text variant="p2" tag="p" className="text-center font-semibold tracking-wide text-white/70">
          Accessing Variant Database /// (1) File Found /// FILE#{nftsFounder?.onchain_tokenId} VARIANT{' '}
          <span className="uppercase">{nftsFounder?.name}</span>
        </Text>

        <div className="mt-6">
          <div className="justify-center border-b-2 border-solid border-[#454340] sm:flex lg:justify-start">
            <div className="mr-0 sm:mr-12 lg:mr-16 xl:mr-28">
              <div className="mb-8">
                <div className="mb-4 flex items-center">
                  <div>
                    <TraitBackgroundIcon
                      color={RarityColor[nftsFounder?.trait_background['rarity_class']]}
                      background={false}
                      width={'51'}
                      height={'61'}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                      background
                    </div>
                    <div
                      className="cursor-default text-xl font-semibold	uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                      title={nftsFounder?.trait_background['value']}
                    >
                      {nftsFounder?.trait_background['value']}
                    </div>
                  </div>
                </div>
                <TraitScoreBar
                  color={RarityColor[nftsFounder?.trait_background['rarity_class']]}
                  score={nftsFounder?.trait_background['rarity']}
                />
              </div>
              <div className="mb-8">
                <div className="mb-4 flex items-center">
                  <div>
                    <TraitBodyIcon
                      color={RarityColor[nftsFounder?.trait_body['rarity_class']]}
                      background={false}
                      width={'51'}
                      height={'61'}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                      body
                    </div>
                    <div
                      className=" cursor-default text-xl font-semibold	uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                      title={nftsFounder?.trait_body['value']}
                    >
                      {nftsFounder?.trait_body['value']}
                    </div>
                  </div>
                </div>
                <TraitScoreBar
                  color={RarityColor[nftsFounder?.trait_body['rarity_class']]}
                  score={nftsFounder?.trait_body['rarity']}
                />
              </div>
              <div className="mb-8">
                <div className="mb-4 flex items-center">
                  <div>
                    <TraitHelmetIcon
                      color={RarityColor[nftsFounder?.trait_helmet['rarity_class']]}
                      background={false}
                      width={'51'}
                      height={'60'}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                      helmet
                    </div>
                    <div
                      className="cursor-default text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                      title={nftsFounder?.trait_helmet['value']}
                    >
                      {nftsFounder?.trait_helmet['value']}
                    </div>
                  </div>
                </div>
                <TraitScoreBar
                  color={RarityColor[nftsFounder?.trait_helmet['rarity_class']]}
                  score={nftsFounder?.trait_helmet['rarity']}
                />
              </div>
              <div className="mb-8">
                <div className="mb-4 flex items-center">
                  <div>
                    <TraitVisorCortexIcon
                      color={RarityColor[nftsFounder?.trait_visor_cortex['rarity_class']]}
                      background={false}
                      width={'51'}
                      height={'60'}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="mb-1.5 cursor-default text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                      visor cortex
                    </div>
                    <div
                      className="cursor-default text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                      title={nftsFounder?.trait_visor_cortex['value']}
                    >
                      {nftsFounder?.trait_visor_cortex['value']}
                    </div>
                  </div>
                </div>
                <TraitScoreBar
                  color={RarityColor[nftsFounder?.trait_visor_cortex['rarity_class']]}
                  score={nftsFounder?.trait_visor_cortex['rarity']}
                />
              </div>
            </div>
            <div>
              <div className="mb-8">
                <div className="mb-4 flex items-center">
                  <div>
                    <TraitLeftShoulderIcon
                      color={RarityColor[nftsFounder?.trait_left_shoulder['rarity_class']]}
                      background={false}
                      width={'51'}
                      height={'60'}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                      LEFT SHOULDER
                    </div>
                    <div
                      className="text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                      title={nftsFounder?.trait_left_shoulder['value']}
                    >
                      {nftsFounder?.trait_left_shoulder['value']}
                    </div>
                  </div>
                </div>
                <TraitScoreBar
                  color={RarityColor[nftsFounder?.trait_left_shoulder['rarity_class']]}
                  score={nftsFounder?.trait_left_shoulder['rarity']}
                />
              </div>
              <div className="mb-8">
                <div className="mb-4 flex items-center">
                  <div>
                    <TraitRightShoulderIcon
                      color={RarityColor[nftsFounder?.trait_right_shoulder['rarity_class']]}
                      background={false}
                      width={'51'}
                      height={'60'}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                      RIGHT SHOULDER
                    </div>
                    <div
                      className="cursor-default text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                      title={nftsFounder?.trait_right_shoulder['value']}
                    >
                      {nftsFounder?.trait_right_shoulder['value']}
                    </div>
                  </div>
                </div>
                <TraitScoreBar
                  color={RarityColor[nftsFounder?.trait_right_shoulder['rarity_class']]}
                  score={nftsFounder?.trait_right_shoulder['rarity']}
                />
              </div>
              <div className="mb-8">
                <div className="mb-4 flex items-center">
                  <div>
                    <TraitWeatherIcon
                      color={RarityColor[nftsFounder?.trait_weather['rarity_class']]}
                      background={false}
                      width={'51'}
                      height={'60'}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="mb-1.5 cursor-default text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                      WEATHER
                    </div>
                    <div
                      className="text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                      title={nftsFounder?.trait_weather['value']}
                    >
                      {nftsFounder?.trait_weather['value']}
                    </div>
                  </div>
                </div>
                <TraitScoreBar
                  color={RarityColor[nftsFounder?.trait_weather['rarity_class']]}
                  score={nftsFounder?.trait_weather['rarity']}
                />
              </div>
              <div className="mb-8">
                <div className="mb-4 flex items-center">
                  <div>
                    <TraitHeldItemIcon
                      color={RarityColor[nftsFounder?.trait_held_item['rarity_class']]}
                      background={false}
                      width={'51'}
                      height={'60'}
                    />
                  </div>
                  <div className="ml-2">
                    <div className="mb-1.5 text-xs font-semibold uppercase leading-[14px] tracking-wide text-white/30">
                      HELD ITEM
                    </div>
                    <div
                      className="text-xl font-semibold uppercase leading-6 tracking-tighter text-white sm:max-w-[185px]"
                      title={nftsFounder?.trait_held_item['value']}
                    >
                      {nftsFounder?.trait_held_item['value']}
                    </div>
                  </div>
                </div>
                <TraitScoreBar
                  color={RarityColor[nftsFounder?.trait_held_item['rarity_class']]}
                  score={nftsFounder?.trait_held_item['rarity']}
                />
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap-reverse items-center justify-center md:flex-nowrap lg:mt-6 lg:justify-start">
            <div className="mx-auto my-4 sm:mx-0 sm:my-0">
              <IconRarity color={RarityColor[nftsFounder?.trait_rarity_class['rarity_class']]} />
            </div>
            <div className="ml-0 w-full sm:ml-6 sm:w-fit">
              <div className="mb-4 text-center text-[19px] leading-4 tracking-tighter text-secondary sm:mb-1.5 sm:text-left">
                {nftsFounder?.trait_squad_role['value'] + ' ///'}
              </div>
              <Text variant="h3" className="text-center tracking-tight sm:text-left">
                {nftsFounder?.name}
              </Text>
            </div>
          </div>
        </div>
      </div>
      <Database />
      <UGCPanel />
      <Faq dataType="variants" />
    </>
  )
}

export default FounderProfilePage
